/**
 * Extension pairing approval (OutlierOS /auth/extension-login replica: checking → approve → success with
 * auto-redirect, or blocked). No manual token fallback: approval needs a signed proof from the extension in
 * this browser (SPEC §8).
 */
import { useEffect, useState } from 'react';
import { CheckCircle2, Loader2, ShieldAlert } from 'lucide-react';
import { BRAND } from '@vw/core/src/brand';
import { Button } from '@vw/ui';
import { api, ApiError } from '../api';
import { AuthShell } from '../components/AuthShell';
import { navigate, useLocation } from '../router';

interface Pairing {
  deviceName: string; userCode: string; status: 'pending' | 'approved' | 'connected' | 'expired'; expiresAt: number;
  browsers: { used: number; limit: number }; extensionIds: string[];
}
declare const chrome: { runtime?: { sendMessage?: (id: string, msg: unknown, cb: (res: unknown) => void) => void; lastError?: unknown } } | undefined;

function askProof(ids: string[], pairingId: string): Promise<string | null> {
  const send = typeof chrome !== 'undefined' ? chrome.runtime?.sendMessage : undefined;
  if (!send || !ids.length) return Promise.resolve(null);
  return new Promise((resolve) => {
    let pending = ids.length;
    let done = false;
    const fail = () => { if (--pending === 0 && !done) resolve(null); };
    for (const id of ids) {
      try {
        send(id, { type: 'vw:pair-proof', pairingId }, (res) => {
          void chrome?.runtime?.lastError;
          const proof = (res as { proof?: unknown } | undefined)?.proof;
          if (!done && typeof proof === 'string') { done = true; resolve(proof); } else fail();
        });
      } catch { fail(); }
    }
  });
}

export function Connect() {
  const { query } = useLocation();
  const id = query.get('p') ?? '';
  const [p, setP] = useState<Pairing | null>(null);
  const [state, setState] = useState<'checking' | 'ready' | 'busy' | 'success' | 'blocked' | 'expired'>('checking');
  const [error, setError] = useState<string | null>(null);
  const [count, setCount] = useState(3);

  useEffect(() => {
    if (!/^[A-Za-z0-9_-]{16,64}$/.test(id)) { setState('expired'); return; }
    api<Pairing>(`/api/ext/pair/${id}`).then((r) => {
      setP(r);
      setState(r.status === 'expired' ? 'expired' : r.status === 'pending' ? (r.browsers.used >= r.browsers.limit ? 'blocked' : 'ready') : 'success');
    }, (e) => { setError(e instanceof ApiError && e.status === 404 ? null : (e as Error).message); setState('expired'); });
  }, [id]);
  useEffect(() => {
    if (state !== 'success') return;
    if (count <= 0) { navigate('/feed'); return; }
    const t = setTimeout(() => setCount(count - 1), 1000);
    return () => clearTimeout(t);
  }, [state, count]);

  async function approve() {
    if (!p) return;
    setState('busy');
    setError(null);
    try {
      const proof = await askProof(p.extensionIds, id);
      if (!proof) throw new Error(`Open this page in the Chrome browser where ${BRAND.name} is installed and where you clicked Connect.`);
      await api(`/api/ext/pair/${id}/approve`, { method: 'POST', json: { deviceProof: proof } });
      setState('success');
    } catch (e) {
      if (e instanceof ApiError && e.code === 'device_limit') setState('blocked');
      // The request expired or was already approved elsewhere: approving again can't work.
      else if (e instanceof ApiError && (e.code === 'gone' || e.code === 'conflict' || e.status === 404)) setState('expired');
      else setState('ready');
      setError((e as Error).message);
    }
  }

  return (
    <AuthShell>
      <h1 className="text-2xl font-extrabold tracking-tight">Connect the Chrome extension</h1>
      <p className="mt-2 text-sm text-muted-foreground">Approve once and the {BRAND.name} extension saves your scans to your library.</p>
      <div className="mt-6 space-y-4">
        {state === 'checking' && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" />Checking your extension…</p>}
        {(state === 'ready' || state === 'busy') && p && (
          <div className="space-y-4 rounded-2xl border border-border bg-card p-5">
            <p className="text-sm">Check that this code matches the one in the extension's side panel:</p>
            <div className="text-center font-mono text-3xl font-black tracking-[0.15em]">{p.userCode}</div>
            <p className="text-xs text-muted-foreground">{p.deviceName} · {p.browsers.used} of {p.browsers.limit} browsers connected on your plan</p>
            <Button size="lg" className="w-full" loading={state === 'busy'} onClick={approve}>Approve</Button>
            <p className="text-xs text-muted-foreground">Only approve if you clicked Connect in the extension just now.</p>
          </div>
        )}
        {state === 'success' && (
          <div className="space-y-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5">
            <p className="flex items-center gap-2 font-bold text-emerald-700 dark:text-emerald-400"><CheckCircle2 className="h-5 w-5" />Extension connected</p>
            <p className="text-sm">Open any creator's Reels tab on Instagram and press Scan in the side panel.</p>
            <Button onClick={() => navigate('/feed')}>Go to your dashboard →</Button>
            <p className="text-xs text-muted-foreground">Taking you there in {count}s…</p>
          </div>
        )}
        {state === 'blocked' && (
          <div className="space-y-3 rounded-2xl border border-rose-500/30 bg-rose-500/10 p-5">
            <p className="flex items-center gap-2 font-bold text-rose-600 dark:text-rose-400"><ShieldAlert className="h-5 w-5" />This browser can't be connected</p>
            <p className="text-sm">{error ?? 'Your plan has reached its connected-browser limit. Disconnect one in Settings or upgrade.'}</p>
            <div className="flex gap-2"><Button variant="secondary" onClick={() => navigate('/settings')}>Open Settings</Button><Button onClick={() => navigate('/billing')}>See plans</Button></div>
            <p className="text-xs text-muted-foreground">Need help? {BRAND.supportEmail}</p>
          </div>
        )}
        {state === 'expired' && <p className="rounded-xl bg-secondary p-4 text-sm text-muted-foreground">{error ?? 'This link is invalid or expired. Click Connect in the extension again.'}</p>}
        {error && (state === 'ready') && <p role="alert" className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      </div>
    </AuthShell>
  );
}
